import { inject, Injectable } from '@angular/core';
import { UIEntityItem } from '../entity-list/ui-entity-item/ui-entity-item';
import { Subject, Observable } from 'rxjs';
import { TabData } from './entity-details-tab/tab-data';
import { TAB_ACTION } from './entity-details-tab/tab-action';
import { DIALOG_VISIBILITY } from '../core/const/dialog-visibility';

import { AlertErrorDataService } from '../alert/alert-error/alert-error-data.service';
import { ALERT_ERROR_NAME } from '../alert/alert-error/alert-error-name';
import { AlertFlashDataService } from '../alert/alert-flash/alert-flash-data.service';
import { UI_ENTITY_ITEM_ACTION } from '../entity-list/const/ui-entity-item-action';

@Injectable({
  providedIn: 'root'
})
export class EntityDetailsDataService {

  private _resetServiceAccess = false;
  private _currentEntityType: string = '';
  private _preventDetailsToClose = false;

  private _readOnly = false;
  private _routerUrl = '';

  private readonly _closeDetails = new Subject<void>();
  public closeDetails$: Observable<void> = this._closeDetails.asObservable();

  private readonly _uiEntityItem = new Subject<UIEntityItem>();
  public uiEntityItem$: Observable<UIEntityItem> = this._uiEntityItem.asObservable();

  // Not the same thing as _uiEntityItem: that one announces a saved entity to the list as well as to
  // the details shell. This one only tells the shell that the item it already holds was mutated in
  // place, so it has to re-read it - the list has nothing to apply, and announcing it there would
  // move focus to the entity column (see EntityListComponent.applyEditedEntity).
  private readonly _refreshUIEntityItem = new Subject<void>();
  public refreshUIEntityItem$: Observable<void> = this._refreshUIEntityItem.asObservable();

  // An application instance's certificate was uploaded or deleted. Deliberately not _uiEntityItem:
  // that one runs the list's applyEditedEntity, which ends in setFocusOn and pulls focus out to the
  // entity options menu - wrong for a panel that stays open. The list only patches the row's upload
  // date from this, so its certificate tag stops showing the previous state.
  private readonly _certificateChanged = new Subject<UIEntityItem>();
  public certificateChanged$: Observable<UIEntityItem> = this._certificateChanged.asObservable();

  private readonly _selectedUIEntityItem = new Subject<UIEntityItem | null>();
  public selectedUIEntityItem$: Observable<UIEntityItem | null> = this._selectedUIEntityItem.asObservable();

  private readonly _detailsDialog = new Subject<string>();
  public detailsDialog$: Observable<string> = this._detailsDialog.asObservable();

  private readonly _discardChangesDialog = new Subject<string>();
  public discardChangesDialog$: Observable<string> = this._discardChangesDialog.asObservable();

  private readonly _confirmationDialog = new Subject<string>();
  public confirmationDialog$: Observable<string> = this._confirmationDialog.asObservable();

  private readonly _tab = new Subject<TabData>();
  public tab$: Observable<TabData> = this._tab.asObservable();

  private readonly _refreshMembersGrid = new Subject<void>();
  public refreshMembersGrid$: Observable<void> = this._refreshMembersGrid.asObservable();

  private readonly _includeAllMembers = new Subject<void>();
  public includeAllMembers$: Observable<void> = this._includeAllMembers.asObservable();

  // Not the same thing as _readOnly: that is the details-level (permission) flag, read once per
  // dialog. This one is the per-members-panel flag, and it has to reach an already-initialised panel.
  private readonly _readOnlyMembers = new Subject<boolean>();
  public readOnlyMembers$: Observable<boolean> = this._readOnlyMembers.asObservable();

  private readonly _password = new Subject<string>();
  public password$: Observable<string> = this._password.asObservable();

  private readonly _resetEditedItem = new Subject<void>();
  public resetEditedItem$: Observable<void> = this._resetEditedItem.asObservable();

  private readonly _spinner = new Subject<boolean>();
  public spinner$: Observable<boolean> = this._spinner.asObservable();

  private readonly alertFlashDataService = inject(AlertFlashDataService);
  private readonly alertErrorDataService = inject(AlertErrorDataService);

  showSpinner(): void {
    this._spinner.next(true);
  }
  
  hideSpinner(): void {
    this._spinner.next(false);
  }

  resetEditedEntity(): void {
    this._resetEditedItem.next();
  }

  closeEntityDetails(): void {
    this._closeDetails.next();
  }

  isPreventDetailsToClose(): boolean {
    return this._preventDetailsToClose;
  }

  clearPreventDetailsToClose(): void {
    this._preventDetailsToClose = false;
  }

  getIncludeAllMembersSessionStorageKey(type: string, id: string, memberType?: string): string {
    if (memberType?.length) {
      return `${type}_${id}_${memberType}`;
    }
    return `${type}_${id}`;
  }

  sendPassword(password: string): void {
    this._password.next(password);
  }

  includeAllMembers(): void {
    this._includeAllMembers.next();
  }

  isReadOnly(): boolean {
    return this._readOnly;
  }

  setReadOnly(value: boolean) {
    this._readOnly = value;
  }

  setReadOnlyMembers(value: boolean): void {
    this._readOnlyMembers.next(value);
  }

  get routerUrl(): string {
    return this._routerUrl;
  }

  set routerUrl(value: string) {
    this._routerUrl = value;
  }

  isResetServiceAccess(): boolean {
    return this._resetServiceAccess;
  }

  setResetServiceAccess(value: boolean) {
    this._resetServiceAccess = value;
  }

  get currentEntityType(): string {
    return this._currentEntityType;
  }

  set currentEntityType(type: string) {
    this._currentEntityType = type;
  }

  clickOnUIEntityItem(uiEntityItem: UIEntityItem): void {
    this._selectedUIEntityItem.next(uiEntityItem);
  }

  clearSelectedUIEntityItem(): void {
    this._preventDetailsToClose = false;
    this._selectedUIEntityItem.next(null);
  }

  createOrUpdateUIEntityItem(uiEntityItem: UIEntityItem): void {
    this._uiEntityItem.next(uiEntityItem);
  }

  refreshUIEntityItem(): void {
    this._refreshUIEntityItem.next();
  }

  certificateChanged(uiEntityItem: UIEntityItem): void {
    this._certificateChanged.next(uiEntityItem);
  }

  deleteUIEntityItem(uiEntityItem: UIEntityItem): void {
    uiEntityItem.action = UI_ENTITY_ITEM_ACTION.delete;
    this._uiEntityItem.next(uiEntityItem);
  }

  changeCompanyStatus(uiEntityItem: UIEntityItem): void {
    uiEntityItem.action = UI_ENTITY_ITEM_ACTION.changeStatus;
    this._uiEntityItem.next(uiEntityItem);
  }

  preventTabChange(): void {
    this._preventDetailsToClose = true;
    this._tab.next({ action: TAB_ACTION.preventChange, tabIndex: -1 });
  }

  allowTabChange(): void {
    this._preventDetailsToClose = false;
    this._tab.next({ action: TAB_ACTION.allowChange, tabIndex: -1 });
  }

  showTab(tabIndex: number): void {
    this._tab.next({ action: TAB_ACTION.show, tabIndex: tabIndex });
  }

  showDetailsDialog(): void {
    this._detailsDialog.next(DIALOG_VISIBILITY.show);
  }

  hideDetailsDialog(): void {
    this.alertErrorDataService.alertErrorName = ALERT_ERROR_NAME.main;
    this._currentEntityType = '';
    this.alertFlashDataService.reset();
    this.alertErrorDataService.reset();
    this._tab.next({ action: TAB_ACTION.resetActive, tabIndex: -1 });
    this._detailsDialog.next(DIALOG_VISIBILITY.hide);
  }

  showDiscardChangesDialog(): void {
    this._discardChangesDialog.next(DIALOG_VISIBILITY.show);
  }

  hideDiscardChangesDialog(): void {
    this._discardChangesDialog.next(DIALOG_VISIBILITY.hide);
  }

  showConfirmationDialog(): void {
    this._confirmationDialog.next(DIALOG_VISIBILITY.show);
  }

  hideConfirmationDialog(): void {
    this._confirmationDialog.next(DIALOG_VISIBILITY.hide);
  }

  hideConfirmationDialogAndRefresh(): void {
    this._confirmationDialog.next(DIALOG_VISIBILITY.hideAndRefresh);
  }

  refreshMembersGrid(): void {
    this._refreshMembersGrid.next();
  }

  copyInputMessage(event: Event) {
    // The trigger is a <button> now, not a <span> - it had to be keyboard-reachable. It is still the
    // input's next sibling, so the previousElementSibling walk is unchanged.
    const trigger = event.currentTarget as HTMLElement;
    const input = trigger.previousElementSibling as HTMLInputElement;
    navigator.clipboard.writeText(input.value);
  }

}
